import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiErrorHidingServerText } from "@/utils";
import queryClient from "@/query-client";
import {
  GlobalProhibitedKeyword,
  GlobalProhibitedKeywordWithRemoval,
  BulkAddProhibitedKeywordsResponse,
  BulkDeleteProhibitedKeywordsResponse,
} from "@/types/prohibited-keyword";
import { DEFAULT_STALE_TIME } from "@/constants";

// AUT-41: Universal (platform-level) prohibited keywords. Admin-only endpoints;
// no brand_id -- the list applies across every brand and product.
const QUERY_KEY = ["global-prohibited-keywords", "list"] as const;

/**
 * AUT-240: the Global prohibited keywords currently in cache, lowercased. Brand and
 * Product optimistic adds use this to skip keywords the Global list already blocks --
 * the server drops them, so without this they flash into the list and then vanish on
 * refetch. Returns an empty set if the Global list hasn't been fetched yet.
 */
export const getGlobalKeywordSet = (): Set<string> =>
  new Set(
    (queryClient.getQueryData<GlobalProhibitedKeyword[]>(QUERY_KEY) ?? []).map(
      (k) => k.keyword.toLowerCase(),
    ),
  );

type Snapshot = { data: GlobalProhibitedKeyword[] | undefined };

const takeSnapshot = (): Snapshot => ({
  data: queryClient.getQueryData<GlobalProhibitedKeyword[]>(QUERY_KEY),
});

const restoreSnapshot = (snapshot: Snapshot) => {
  queryClient.setQueryData(QUERY_KEY, snapshot.data);
};

let optimisticIdCounter = -1;

export const useGetGlobalProhibitedKeywords = () =>
  useQuery<GlobalProhibitedKeyword[]>({
    queryKey: QUERY_KEY,
    queryFn: () =>
      apiService.get<GlobalProhibitedKeyword[]>("/global-prohibited-keywords"),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useAddGlobalProhibitedKeyword = () =>
  useMutation<
    GlobalProhibitedKeywordWithRemoval,
    unknown,
    { keyword: string; origin?: string },
    { snapshot: Snapshot }
  >({
    mutationFn: (data) =>
      apiService.post<GlobalProhibitedKeywordWithRemoval>(
        "/global-prohibited-keywords",
        data,
      ),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const snapshot = takeSnapshot();

      const optimistic: GlobalProhibitedKeyword = {
        id: optimisticIdCounter--,
        keyword: variables.keyword.trim().toLowerCase(),
        origin: (variables.origin as GlobalProhibitedKeyword["origin"]) ?? "manual",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<GlobalProhibitedKeyword[]>(QUERY_KEY, (prev) =>
        prev ? [...prev, optimistic] : [optimistic],
      );

      return { snapshot };
    },
    onError: (error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiErrorHidingServerText(error);
    },
    onSuccess: (serverData, variables) => {
      queryClient.setQueryData<GlobalProhibitedKeyword[]>(QUERY_KEY, (prev) => {
        if (!prev) return prev;
        return prev.map((k) =>
          k.id < 0 && k.keyword === variables.keyword.trim().toLowerCase()
            ? serverData
            : k,
        );
      });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });

export const useBulkAddGlobalProhibitedKeywords = () =>
  useMutation<
    BulkAddProhibitedKeywordsResponse,
    unknown,
    { keywords: string[]; origin?: string }
  >({
    mutationFn: (data) =>
      apiService.post<BulkAddProhibitedKeywordsResponse>(
        "/global-prohibited-keywords/bulk",
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
    onError: (error) => handleApiErrorHidingServerText(error),
  });

export const useDeleteGlobalProhibitedKeyword = () =>
  useMutation<unknown, unknown, { id: number }, { snapshot: Snapshot }>({
    mutationFn: ({ id }) => apiService.delete(`/global-prohibited-keywords/${id}`),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const snapshot = takeSnapshot();

      queryClient.setQueryData<GlobalProhibitedKeyword[]>(QUERY_KEY, (prev) =>
        prev ? prev.filter((k) => k.id !== variables.id) : prev,
      );

      return { snapshot };
    },
    onError: (error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiErrorHidingServerText(error);
    },
    onSettled: () => {
      // AUT-220: reconcile the list with the server after the delete settles. Without
      // this the optimistic write was the only source of truth (staleTime is 5 min),
      // so a rolled-back failure could resurrect a row a later delete had removed.
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });

// Bulk remove. No snapshot: a bulk request the SPA sees fail may have half-succeeded
// on the server, so the failure path re-reads the list instead of restoring it.
export const useBulkDeleteGlobalProhibitedKeywords = () =>
  useMutation<BulkDeleteProhibitedKeywordsResponse, unknown, { ids: number[] }>({
    mutationFn: ({ ids }) =>
      apiService.post<BulkDeleteProhibitedKeywordsResponse, { ids: number[] }>(
        "/global-prohibited-keywords/bulk-delete",
        { ids },
      ),
    onMutate: async ({ ids }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const removed = new Set(ids);
      queryClient.setQueryData<GlobalProhibitedKeyword[]>(QUERY_KEY, (prev) =>
        prev ? prev.filter((k) => !removed.has(k.id)) : prev,
      );
    },
    onError: (error) => handleApiErrorHidingServerText(error),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });
